import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Menu, Sun, Moon, HelpCircle, LogOut, LayoutGrid } from 'lucide-react';
import GlobalSearch from '../GlobalSearch';
import NotificationBell from '../NotificationBell';
import LocalPrintQueueButton from '../LocalPrintQueueButton';

// iter 214 — Extracted from Layout.js (was lines 679-777). Visual JSX unchanged.
//
// Props:
//   onMobileMenuOpen — () => void to open the sidebar on mobile
//   onSupportOpen    — () => void to open SupportModal
//   onLogout         — () => Promise<void> (used by mobile-only quick logout)
//   theme            — 'light' | 'dark'
//   toggleTheme      — () => void
//   location         — react-router location (for active highlight on /dashboard)
//   t                — i18n.t function
export default function AppHeader({
  onMobileMenuOpen,
  onSupportOpen,
  onLogout,
  theme,
  toggleTheme,
  location,
  t,
}) {
  const isDashboardRoute = ['/', '/dashboard-v2', '/irányitopult'].includes(location.pathname);

  return (
    <div
      className="sticky top-0 z-20 glass border-b px-4 md:px-8 py-3 flex items-center gap-3"
      style={{ borderColor: 'var(--border)', paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
    >
      <button
        onClick={onMobileMenuOpen}
        data-testid="sidebar-open-btn"
        className="md:hidden p-2.5 rounded-xl flex items-center justify-center"
        style={{
          backgroundColor: 'var(--accent-copper)',
          color: 'var(--accent-copper-fg)',
        }}
        aria-label="Menü megnyitása"
      >
        <Menu size={20} strokeWidth={2.25} />
      </button>

      <div className="flex-1 min-w-0">
        <GlobalSearch />
      </div>

      {/* iter 310 — „Áttekintés” lebegő (úszó) üveg-pirula: portálban a body-ra, mert a sticky+blur fejléc elkapná a position:fixed-et */}
      {createPortal(
      <Link
        to="/"
        data-testid="header-dashboard-quick"
        aria-label={t('nav.overview', 'Áttekintés')}
        title={t('nav.overview', 'Áttekintés')}
        className="fixed z-40 flex items-center gap-2 rounded-full px-4 h-11 transition-transform hover:scale-105 active:scale-95 left-1/2 -translate-x-1/2 bottom-20 md:bottom-5 md:left-[calc(50%+9rem)]"
        style={{
          top: 'auto',
          background: isDashboardRoute ? 'var(--accent-copper)' : 'rgba(15,23,42,0.88)',
          color: '#FFFFFF', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.18)', boxShadow: 'var(--shadow-floating, 0 12px 36px -4px rgba(15,23,42,0.35))',
        }}
      >
        <LayoutGrid size={15} strokeWidth={2.25} />
        <span className="font-semibold whitespace-nowrap" style={{ fontSize: '13px', letterSpacing: '-0.015em' }}>{t('nav.overview', 'Áttekintés')}</span>
      </Link>
      , document.body)}

      <button
        onClick={toggleTheme}
        data-testid="theme-toggle"
        className="p-2 rounded-lg transition-colors hidden md:block"
        style={{ color: 'var(--text-secondary)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--background-base)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        aria-label={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
        title={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
      >
        {theme === 'dark' ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
      </button>

      <NotificationBell />
      <LocalPrintQueueButton />

      <button
        onClick={onSupportOpen}
        data-testid="support-open-btn"
        className="p-2 rounded-lg transition-colors hidden md:flex items-center justify-center"
        style={{ color: 'var(--accent-copper)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-copper-soft)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        aria-label={t('nav.support')}
        title={t('nav.supportTooltip')}
      >
        <HelpCircle size={18} strokeWidth={2} />
      </button>

      <button
        onClick={onLogout}
        data-testid="logout-button-mobile"
        className="md:hidden p-2 rounded-lg transition-colors"
        style={{ color: '#DC2626' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(220,38,38,0.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        aria-label={t('nav.logout')}
        title={t('nav.logout')}
      >
        <LogOut size={18} strokeWidth={1.9} />
      </button>
    </div>
  );
}
